import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";
import { z } from "zod";

import { faCirclePlus, faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, InputText, Table, type TableRef } from "@performyard/py-components";
import type { ReviewCycleReportRowFrontend } from "@performyard/service-contracts";
import { DEFAULT_REVIEW_CYCLE_DUE_TIME, DEFAULT_TIMEZONE } from "@performyard/utils-isomorphic";

import Spacer from "../../../../components/Spacer";
import BulkActionModal from "./components/BulkActionModal";
import SelectionBanner from "./components/SelectionBanner";

import { useRbacObjectPermissions, useReviewCycleReportsStream } from "../../../../api/hooks";
import { useCompanySettings } from "../../../../api/hooks/defs/company";
import useSearchQueryParams from "../../../../hooks/useSearchQueryParams";
import { useReviewFormsBulkActions } from "./hooks/useReviewFormsBulkActions";
import { useReviewFormsSelection } from "./hooks/useReviewFormsSelection";

import { TABLE_HEADER_HEIGHT, TABLE_ROW_HEIGHT, getTableSizing } from "../table-height";
import { type BulkActionKey, getAvailableBulkActions, getBulkAction } from "./bulk-actions";
import { SELECTION_COLUMN_ID, type SelectionColumnOptions, getReviewFormsColumns } from "./columns";

// Kept in the URL (like the Review Subjects search) so it survives switching tabs
const FormsSearchSchema = z.object({
	formSearch: z.string().default(""),
});

const ReviewFormsTable = ({ reviewCycleId }: { reviewCycleId: string }) => {
	const navigate = useNavigate();
	const { data: rows, isFetching, isError } = useReviewCycleReportsStream(reviewCycleId);

	const { data: permissions = [] } = useRbacObjectPermissions("review", reviewCycleId);
	const canAddForms = permissions.includes("review_cycle:addFormsInCycle");
	const canRunBulkActions = getAvailableBulkActions(permissions).length > 0;

	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;
	const reviewSettings = companySettings?.functions?.reviews as
		| { default_review_cycle_due_time?: number }
		| undefined;
	const defaultDueTime = reviewSettings?.default_review_cycle_due_time ?? DEFAULT_REVIEW_CYCLE_DUE_TIME;

	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: FormsSearchSchema });
	const search = queryParams.formSearch;
	const [debouncedSearch] = useDebounceValue(search, 200);
	const [openActionKey, setOpenActionKey] = useState<BulkActionKey | null>(null);

	const allRows = useMemo(() => rows ?? [], [rows]);

	// Sorted here rather than with a column sort so the default order shows no sort indicators
	const filteredRows = useMemo(() => {
		const term = debouncedSearch.trim().toLowerCase();
		const matching = term ? allRows.filter((row) => row.form.name.toLowerCase().includes(term)) : [...allRows];

		return matching.sort(
			(a, b) => a.form.name.localeCompare(b.form.name) || a.subject.name.localeCompare(b.subject.name)
		);
	}, [allRows, debouncedSearch]);

	const selection = useReviewFormsSelection({ allRows, filteredRows });
	const {
		selectedCount,
		selectedIds,
		totalCount,
		isRowSelected,
		toggleRow,
		setFilteredRowsSelected,
		selectAllRows,
		clearSelection,
		allFilteredRowsSelected,
		allRowsSelected,
	} = selection;

	const closeModal = useCallback(() => setOpenActionKey(null), []);

	const { applyUpdates, confirmDelete, isSaving } = useReviewFormsBulkActions({
		reviewCycleId,
		selectedIds,
		clearSelection,
		closeModal,
	});

	// The selection column reads from this ref so `columns` stays stable; re-supplying columnDefs
	// would make AG Grid re-process every column on each checkbox click.
	const selectionRef = useRef<SelectionColumnOptions>({
		isRowSelected,
		onToggleRow: toggleRow,
		allFilteredRowsSelected,
		onToggleFilteredRows: setFilteredRowsSelected,
		hasRows: filteredRows.length > 0,
	});

	const columns = useMemo(
		() =>
			getReviewFormsColumns({
				companyTimezone,
				selectionRef: canRunBulkActions ? selectionRef : undefined,
			}),
		[canRunBulkActions, companyTimezone]
	);

	// Sync the ref and repaint together; the grid renders these cells outside React, so the refresh
	// is what makes the new ref visible.
	const gridRef = useRef<TableRef<ReviewCycleReportRowFrontend>>(null);
	useEffect(() => {
		selectionRef.current = {
			isRowSelected,
			onToggleRow: toggleRow,
			allFilteredRowsSelected,
			onToggleFilteredRows: setFilteredRowsSelected,
			hasRows: filteredRows.length > 0,
		};

		const api = gridRef.current?.api;
		if (!api || api.isDestroyed()) return;
		api.refreshCells({ columns: [SELECTION_COLUMN_ID], force: true });
		api.refreshHeader();
	}, [isRowSelected, toggleRow, allFilteredRowsSelected, setFilteredRowsSelected, filteredRows, selectedIds]);

	// Includes rows the search is currently hiding, matching what a bulk action would apply to
	const selectedRows = useMemo(() => allRows.filter((row) => selectedIds.has(row._id)), [allRows, selectedIds]);

	// The search filters rowData here rather than through the grid, so what the grid displays is
	// exactly filteredRows
	const { autoHeight, height: tableHeight } = getTableSizing(filteredRows.length);

	const openAction = openActionKey ? getBulkAction(openActionKey) : null;

	return (
		<div className="flex-col gap-2">
			<div className="flex w-full items-center justify-between gap-2">
				<div className="w-full max-w-[278px] min-w-[278px]">
					<InputText
						id="review-forms-search"
						placeholder="Search forms..."
						aria-label="Search forms"
						leftIcon={faMagnifyingGlass}
						value={search}
						onChange={(value) => handleQueryChange({ formSearch: value })}
						clearable={search !== ""}
					/>
				</div>
				{canAddForms && (
					<ButtonStandard
						color="neutral"
						variant="outline"
						size="medium"
						iconLeft={faCirclePlus}
						onClick={() => navigate(`/review-cycle/${reviewCycleId}/add-forms/`)}
						dangerouslySetClassName="w-auto"
						data-testid="review-forms-add-forms"
					>
						Add Forms
					</ButtonStandard>
				)}
			</div>
			<Spacer height={2} />

			{canRunBulkActions && selectedCount > 0 && (
				<>
					<SelectionBanner
						selectedCount={selectedCount}
						totalCount={totalCount}
						allRowsSelected={allRowsSelected}
						permissions={permissions}
						// A bulk action mid-stream would only reach the rows already cached
						disabled={isSaving || isFetching}
						isLoadingRows={isFetching}
						onSelectAll={selectAllRows}
						onClearSelection={clearSelection}
						onActionSelected={setOpenActionKey}
					/>
					<Spacer height={2} />
				</>
			)}

			<div className="w-full" style={tableHeight === undefined ? undefined : { height: tableHeight }}>
				<Table<ReviewCycleReportRowFrontend>
					ref={gridRef}
					autoHeight={autoHeight}
					columnSizing="fit-grid"
					rowData={filteredRows}
					columnDefs={columns}
					getRowId={({ data }) => data._id}
					rowHeight={TABLE_ROW_HEIGHT}
					headerHeight={TABLE_HEADER_HEIGHT}
					loading={isFetching && allRows.length === 0}
					emptyStateText={
						isError ? "We couldn't load the review forms. Please refresh to try again." : "No results found"
					}
					suppressMultiSort={true} // Only ever show one sort indicator
					suppressScrollOnNewData={true}
					suppressCellFocus={true}
					suppressRowHoverHighlight={true}
				/>
			</div>

			{openAction && (
				<BulkActionModal
					action={openAction}
					selectedCount={selectedCount}
					selectedRows={selectedRows}
					companyTimezone={companyTimezone}
					defaultDueTime={defaultDueTime}
					isSaving={isSaving}
					onClose={closeModal}
					onApplyUpdates={applyUpdates}
					onConfirmDelete={confirmDelete}
				/>
			)}
		</div>
	);
};

export default ReviewFormsTable;
